import React from 'react';
import PropTypes from 'prop-types';
import NotificationLog from '../../../../common/models/NotificationLog';
import { NotificationStatus } from '../../../../components/NotificationLog';
import LocalDate from '../../../../components/plates/LocalDate';
import { getEventBackgroundColor, getEventBorderColor } from './helpers';

const TimelineEvent = ({
  'data-testid': testId,
  type,
  status,
  icon,
  title,
  name,
  date,
  projectId,
  isEmpty,
  onClick,
  onStatusClick,
  reviewIcon,
  notification,
  isNonActionable,
  isDisabled,
  isClinicalEvent,
}) => {
  return (
    <div className="relative group stack-0 w-full">
      {/* NOTE: This div is extra background to make sure what opaque milestones */}
      {/* don't show through underlying elements. For example, without it */}
      {/* the timeline middle line shows through discharged milestones */}
      {/* SEE: https://theclinician.atlassian.net/browse/TECH-1543 */}
      <div className="absolute inset-0 bg-surface rounded-lg z-0" />
      {title && (
        <div
          className={`z-10 ${getEventBorderColor(
            type,
            isEmpty,
            isDisabled,
          )} border-r-2 border-t-2 border-l-2 rounded-tl-lg rounded-tr-lg cluster-2 items-center justify-center p-2 transition-colors`}
        >
          {icon && <div className="w-4 h-4">{icon}</div>}
          <span className="text-xs font-medium truncate">{title}</span>
        </div>
      )}
      <button
        data-testid={testId}
        type="button"
        onClick={isDisabled ? () => {} : onClick}
        className={`z-10 ${
          !notification ? 'rounded-bl-lg rounded-br-lg' : ''
        } ${getEventBackgroundColor(
          type,
          isEmpty,
          isNonActionable || isClinicalEvent,
          isDisabled,
          !!notification,
        )} ${getEventBorderColor(type, isEmpty, isDisabled)} ${
          isDisabled ? 'cursor-not-allowed' : ''
        } border-2 p-4 stack-0 items-center w-full transition-colors`}
      >
        {reviewIcon && (
          <div
            className={`absolute -right-2 -top-2 p-1 rounded-full bg-surface transition-colors border-2 ${getEventBorderColor(
              type,
              isEmpty,
              isDisabled,
            )}`}
          >
            {reviewIcon}
          </div>
        )}
        <p className="self-stretch font-medium text-sm truncate">{name}</p>
        <span className="self-stretch text-xs truncate">
          {status && <span>{status}</span>}
          {status && date && <span> • </span>}
          {date && <LocalDate date={date} projectId={projectId} short />}
        </span>
      </button>
      {notification ? (
        <div
          className={`z-10 ${getEventBorderColor(
            type,
            isEmpty,
            isDisabled,
          )} border-r-2 border-b-2 border-l-2 rounded-bl-lg rounded-br-lg transition-colors`}
        >
          <NotificationStatus
            projectId={projectId}
            deliveryType={notification.getType()}
            state={notification.getState()}
            timestamp={notification.getTimestamp()}
            onClick={onStatusClick}
            block
          />
        </div>
      ) : null}
    </div>
  );
};

TimelineEvent.propTypes = {
  'data-testid': PropTypes.string,
  name: PropTypes.string.isRequired,
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  type: PropTypes.oneOf(['success', 'warning', 'info', 'danger']),
  status: PropTypes.string,
  icon: PropTypes.node,
  title: PropTypes.string,
  reviewIcon: PropTypes.node,
  notification: PropTypes.instanceOf(NotificationLog),
  projectId: PropTypes.string,
  isEmpty: PropTypes.bool,
  isNonActionable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func,
  onStatusClick: PropTypes.func,
};

TimelineEvent.defaultProps = {
  'data-testid': 'milestone',
  type: 'neutral',
  status: null,
  icon: null,
  reviewIcon: null,
  title: null,
  date: null,
  projectId: null,
  notification: null,
  isEmpty: false,
  isNonActionable: false,
  isDisabled: false,
  onClick: () => {},
  onStatusClick: () => {},
};

export default TimelineEvent;
